import { useState } from "react";
import { Tooltip } from "react-tooltip";

import { REACT_APP_DETAIL_GUARANTEES_URL } from "../../../constants/env";
import { FormulaId } from "../data/savings";
import { redirectToSouscription } from "../helpers/souscription";
import { openZendeskWidget } from "../helpers/zendesk";
import {
  OfferboardChip,
  useZeroFranchiseTracking,
} from "../hooks/useZeroFranchiseTracking";
import { Button } from "./Button";
import { ChevronRightIcon } from "./icons/ChevronRightIcon";
import { FileIcon } from "./icons/FileIcon";
import { InfoIcon } from "./icons/InfoIcon";
import { SavingsTable } from "./SavingsTable";

const FRANCHISE_TOOLTIP_ID = "savings-franchise-tooltip";
const FRANCHISE_TOOLTIP_CONTENT =
  "La possibilité de modifier votre franchise dépend de votre contrat et de votre assureur principal. Pensez à vérifier les conditions avec lui avant toute modification.";

const TOOLTIP_CLASS_NAME =
  "!z-10 !max-w-[300px] !rounded-[16px] !bg-white !text-[13px] !leading-[1.6] !tracking-[0.26px] !text-[#0D0D0D] !opacity-100 drop-shadow-[0_2px_6px_rgba(0,0,0,0.12)]";

const FORMULAS: ReadonlyArray<{
  id: FormulaId;
  label: string;
  icon: string;
  chips: OfferboardChip;
}> = [
  {
    id: "all-risks",
    label: "Tous risques",
    icon: "/assets/pack-zero-franchise/aide-aux-reparations.svg",
    chips: "tousrisque",
  },
  {
    id: "third-party",
    label: "Au tiers",
    icon: "/assets/pack-zero-franchise/rachat-de-franchise.svg",
    chips: "autiers",
  },
];

const WOMAN_CAR_IMAGE = "/assets/pack-zero-franchise/woman-car.png";

export const SavingsSection = () => {
  const [activeFormula, setActiveFormula] = useState<FormulaId>("all-risks");
  const { trackOnlineSubscription, trackRecall, trackOfferboard } = useZeroFranchiseTracking();

  const handleSubscribe = () => {
    trackOnlineSubscription("tableau_franchises");
    redirectToSouscription();
  };

  const handleRecall = () => {
    trackRecall("tableau_franchises");
    openZendeskWidget();
  };

  const handleFormulaClick = (formula: typeof FORMULAS[number]) => {
    setActiveFormula(formula.id);
    trackOfferboard(formula.chips);
  };

  return (
    <section className="w-full flex justify-center">
      <div className="xl:mx-28 rounded-M md:rounded-[40px] bg-[#FFD100] max-md:pb-4 py-16 xl:max-w-[1214px] xl:py-[120px]">
        <div className="px-4 md:px-10 xl:px-20">
          <div className="mx-auto flex max-w-[850px] flex-col items-center gap-3 text-center text-[#0D0D0D]">
            <h2 className="text-[23px] font-bold leading-[1.2] md:text-[28px]">
              <span className="md:hidden">Faites des économies</span>
              <span className="hidden md:inline">Vous faites des économies</span>
            </h2>
            <p className="text-[16px] leading-[1.6] md:text-[19px]">
              Votre assureur paie les réparations, Roole prend le relais et rembourse votre
              franchise jusqu’à 500&nbsp;€.
            </p>
          </div>

          <div className="mt-12 flex flex-col items-center gap-8">
            <div className="flex items-center gap-3" role="tablist">
              {FORMULAS.map((formula) => {
                const isActive = activeFormula === formula.id;

                return (
                  <button
                    key={formula.id}
                    type="button"
                    role="tab"
                    aria-selected={isActive}
                    onClick={() => handleFormulaClick(formula)}
                    className={[
                      "inline-flex h-10 items-center gap-1 rounded-full px-4 py-2 text-[16px] transition-colors",
                      isActive
                        ? "bg-[#0D0D0D] text-white"
                        : "bg-white text-[#0D0D0D] hover:bg-white/80",
                    ].join(" ")}
                  >
                    <img
                      src={formula.icon}
                      alt=""
                      width={20}
                      height={20}
                      className={
                        isActive ? "[filter:brightness(0)_invert(1)]" : "[filter:brightness(0)]"
                      }
                    />
                    {formula.label}
                  </button>
                );
              })}
            </div>

            <SavingsTable formulaId={activeFormula} />
          </div>

          <div className="mt-12 flex flex-col items-center gap-10">
            <button
              type="button"
              className="inline-flex items-center gap-1 text-[13px] leading-5 text-[#0D0D0D] underline underline-offset-4 hover:[font-weight:600]"
              onClick={() => window.open(REACT_APP_DETAIL_GUARANTEES_URL, "_blank", "noopener noreferrer")}
            >
              <FileIcon size={24} />
              <span>Voir le détail des garanties</span>
              <ChevronRightIcon size={24} />
            </button>
            <div className="flex w-full max-w-[744px] flex-col gap-1 text-left text-[13px] leading-[1.6] tracking-[0.26px] text-[#0D0D0D]">
              <p>
                <sup>*</sup> Données basées sur les remboursements de franchise observés chez les
                membres Roole en 2025, et des données marché (Goodassur, Événements climatiques).
              </p>
              <p>
                <sup>**</sup> Franchise remboursée jusqu’à 500 € sans limite par an, sauf pour le
                vol de pièces et les événements climatiques, limités à 1 sinistre couvert par an.
              </p>
            </div>
          </div>
        </div>

        <div className="mt-20 px-4 md:px-10 xl:px-20">
          <div className="flex flex-col items-center gap-8 md:gap-14 xl:grid xl:grid-cols-[minmax(0,_3fr)_minmax(0,_2fr)] xl:items-end">
            <div className="flex w-full flex-col gap-8 md:max-w-[524px] xl:max-w-none">
              <div className="flex flex-col items-center gap-4">
                <img
                  src="/assets/pack-zero-franchise/savings-sticker.svg"
                  alt=""
                  width={116}
                  height={92}
                  className="h-auto w-[116px]"
                />
                <h3 className="text-center text-[23px] font-bold leading-[1.2] text-[#0D0D0D] md:text-[28px] xl:text-left">
                  Payez moins, soyez mieux couvert. La&nbsp;combinaison gagnante&nbsp;!
                </h3>
              </div>

              <div className="flex flex-col gap-6">
                <div className="flex items-start gap-3">
                  <span className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-[#EEC300] text-[16px] font-semibold leading-[1.2] text-[#0D0D0D]">
                    1
                  </span>
                  <p className="text-[16px] leading-[1.6] text-[#0D0D0D]">
                    Augmentez votre franchise à{" "}
                    <span className="[font-weight:600]">500&nbsp;€</span> chez votre assureur&nbsp;:
                    votre prime peut baisser, parfois dès le mois suivant.{" "}
                    <button
                      type="button"
                      aria-label="Plus d’informations"
                      data-tooltip-id={FRANCHISE_TOOLTIP_ID}
                      data-tooltip-content={FRANCHISE_TOOLTIP_CONTENT}
                      className="inline-flex cursor-pointer items-center align-middle"
                    >
                      <InfoIcon size={16} />
                    </button>
                  </p>
                </div>
                <div className="flex items-start gap-3">
                  <span className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-[#EEC300] text-[16px] font-semibold leading-[1.2] text-[#0D0D0D]">
                    2
                  </span>
                  <p className="text-[16px] leading-[1.6] text-[#0D0D0D]">
                    Le jour où vous en avez besoin,&nbsp;
                    <span className="[font-weight:600]">Roole rembourse votre franchise.</span>
                  </p>
                </div>
              </div>

              <div className="flex w-full max-md:self-center max-md:max-w-[280px] flex-col items-stretch gap-4 md:w-auto md:flex-row md:items-center md:justify-center xl:justify-start">
                <Button
                  variant="primary-dark"
                  className="w-full md:w-auto"
                  onClick={handleSubscribe}
                >
                  Souscrire en ligne
                </Button>
                <Button
                  variant="outline-dark"
                  className="w-full md:w-auto"
                  onClick={handleRecall}
                >
                  Me faire rappeler
                </Button>
              </div>
            </div>

            <div className="w-full overflow-hidden rounded-[24px] xl:rounded-[32px]">
              <img
                src={WOMAN_CAR_IMAGE}
                alt="Femme à côté de sa voiture"
                width={1208}
                height={682}
                className="block h-auto w-full xl:h-[377px] xl:object-cover"
              />
            </div>
          </div>
        </div>
      </div>
      <Tooltip
        id={FRANCHISE_TOOLTIP_ID}
        place="bottom"
        openEvents={{ mouseenter: true, click: true, focus: true }}
        closeEvents={{ mouseleave: true, blur: true }}
        globalCloseEvents={{ scroll: true, escape: true, clickOutsideAnchor: true }}
        className={TOOLTIP_CLASS_NAME}
        classNameArrow="!h-[15px] !w-[15px] !bg-white"
      />
    </section>
  );
};
